import { useState, useEffect } from 'react'
import { supabase } from './lib/supabaseClient'
import MessagingSystem from './components/MessagingSystem'
import WhatsAppButton from './components/WhatsAppButton'
import PortalLayout from './components/PortalLayout'
import { responsiveStyles } from './styles/responsiveStyles'

function RecruitmentPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [jobs, setJobs] = useState([])
  const [applicants, setApplicants] = useState([])
  const [interviews, setInterviews] = useState([])
  const [offers, setOffers] = useState([])
  const [emailTemplates, setEmailTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('recruitment')
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [uploading, setUploading] = useState(false)
  const [selectedApplicant, setSelectedApplicant] = useState(null)
  const [emailCompose, setEmailCompose] = useState({ to: '', subject: '', body: '', applicant_id: null })
  const [stats, setStats] = useState({
    totalJobs: 0,
    openJobs: 0,
    totalApplicants: 0,
    pendingApplicants: 0,
    scheduledInterviews: 0,
    offersSent: 0,
    hiredCount: 0
  })

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'jobs', label: 'Job Postings', icon: '📋' },
    { id: 'applicants', label: 'Applicants', icon: '👥' },
    { id: 'interviews', label: 'Interviews', icon: '📅' },
    { id: 'offers', label: 'Offers', icon: '📄' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' },
    { id: 'emails', label: 'Emails', icon: '📧' }
  ]

  const departments = ['Sales', 'Marketing', 'Accounting', 'Legal', 'IT', 'HR', 'Operations', 'Management']
  const employmentTypes = ['full-time', 'part-time', 'contract', 'internship', 'remote']
  const experienceLevels = ['entry', 'mid', 'senior', 'lead', 'executive']
  const interviewTypes = ['phone', 'video', 'onsite', 'technical', 'hr']
  const applicantStatuses = ['pending', 'reviewed', 'shortlisted', 'interviewed', 'offered', 'hired', 'rejected']

  // Get company ID from session
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) return

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching company_id:', error)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        return
      }

      setCompanyId(userData.company_id)
      
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .maybeSingle()
      
      if (company) {
        setCompanyName(company.name)
      }
    }

    getCompanyId()
  }, [session])

  // Fetch all data when companyId is available
  useEffect(() => {
    if (companyId) {
      fetchAllData()
      fetchUnreadMessages()
    }
  }, [activeTab, companyId])

  const fetchUnreadMessages = async () => {
    if (!companyId) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)
    
    setUnreadCount(count || 0)
  }

  const sendEmail = async () => {
    if (!emailCompose.to || !emailCompose.body) {
      alert('Please fill in recipient and message')
      return
    }

    const { error } = await supabase.from('emails').insert({
      to_email: emailCompose.to,
      subject: emailCompose.subject || 'No Subject',
      body: emailCompose.body,
      from_email: session.user.email,
      direction: 'outbound',
      agent_id: session.user.id,
      applicant_id: emailCompose.applicant_id || null,
      company_id: companyId
    })

    if (error) {
      alert('Failed to send: ' + error.message)
    } else {
      alert('Email sent successfully!')
      setEmailCompose({ to: '', subject: '', body: '', applicant_id: null })
      setShowEmailModal(false)
    }
  }

  const fetchAllData = async () => {
    if (!companyId) return
    setLoading(true)
    
    try {
      // Fetch jobs
      const { data: jobsData } = await supabase
        .from('job_postings')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      setJobs(jobsData || [])
      
      // Fetch applicants
      const { data: applicantsData } = await supabase
        .from('applicants')
        .select('*, job_postings(title)')
        .eq('company_id', companyId)
        .order('applied_date', { ascending: false })
      setApplicants(applicantsData || [])
      
      // Fetch interviews
      const { data: interviewsData } = await supabase
        .from('interviews')
        .select('*, applicants(first_name, last_name, email, phone), job_postings(title)')
        .eq('company_id', companyId)
        .order('scheduled_date', { ascending: true })
      setInterviews(interviewsData || [])
      
      // Fetch offers
      const { data: offersData } = await supabase
        .from('offers')
        .select('*, applicants(first_name, last_name, email, phone), job_postings(title)')
        .eq('company_id', companyId)
      setOffers(offersData || [])
      
      // Fetch email templates
      const { data: templatesData } = await supabase
        .from('email_templates')
        .select('*')
        .eq('company_id', companyId)
      setEmailTemplates(templatesData || [])
      
      // Calculate stats
      const openJobs = jobsData?.filter(j => j.status === 'open' || j.status === 'active').length || 0
      const pendingApplicants = applicantsData?.filter(a => a.status === 'pending' || a.status === 'reviewed').length || 0
      const scheduledInterviews = interviewsData?.filter(i => i.status === 'scheduled').length || 0
      const offersSent = offersData?.filter(o => o.status === 'sent' || o.status === 'accepted').length || 0
      const hiredCount = applicantsData?.filter(a => a.status === 'hired').length || 0
      
      setStats({
        totalJobs: jobsData?.length || 0,
        openJobs: openJobs,
        totalApplicants: applicantsData?.length || 0,
        pendingApplicants: pendingApplicants,
        scheduledInterviews: scheduledInterviews,
        offersSent: offersSent,
        hiredCount: hiredCount
      })
    } catch (err) {
      console.error('Error fetching data:', err)
    }
    
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      let table = ''
      let data = { ...formData, company_id: companyId }

      if (activeTab === 'jobs') {
        table = 'job_postings'
        data.salary_min = parseFloat(data.salary_min) || 0
        data.salary_max = parseFloat(data.salary_max) || 0
        data.requirements = data.requirements || []
        data.benefits = data.benefits || []
      } else if (activeTab === 'applicants') {
        table = 'applicants'
        data.experience_years = parseInt(data.experience_years) || 0
        data.expected_salary = parseFloat(data.expected_salary) || 0
        data.skills = data.skills || []
        data.status = data.status || 'pending'
      } else if (activeTab === 'interviews') {
        table = 'interviews'
        data.duration_minutes = parseInt(data.duration_minutes) || 60
        data.status = data.status || 'scheduled'
      } else if (activeTab === 'offers') {
        table = 'offers'
        data.salary_offered = parseFloat(data.salary_offered) || 0
        data.benefits = data.benefits || []
        data.status = data.status || 'draft'
      }

      if (editingItem) {
        await supabase.from(table).update(data).eq('id', editingItem.id)
      } else {
        await supabase.from(table).insert(data)
      }

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      fetchAllData()
    } catch (err) {
      alert('Error: ' + err.message)
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (confirm('Are you sure?')) {
      let table = ''
      if (activeTab === 'jobs') table = 'job_postings'
      else if (activeTab === 'applicants') table = 'applicants'
      else if (activeTab === 'interviews') table = 'interviews'
      else if (activeTab === 'offers') table = 'offers'
      
      await supabase.from(table).delete().eq('id', id)
      fetchAllData()
    }
  }

  const handleResumeUpload = async (file) => {
    if (!file) return null
    setUploading(true)
    const fileName = `${companyId}_resume_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`
    
    const { error: uploadError } = await supabase.storage.from('resumes').upload(fileName, file)
    if (uploadError) {
      alert('Upload failed: ' + uploadError.message)
      setUploading(false)
      return null
    }
    
    const { data: urlData } = supabase.storage.from('resumes').getPublicUrl(fileName)
    setUploading(false)
    return urlData.publicUrl
  }

  const updateApplicantStatus = async (applicantId, status) => {
    await supabase.from('applicants').update({ status }).eq('id', applicantId)
    fetchAllData()
  }

  const renderForm = () => {
    const inputStyle = { width: '100%', padding: 10, margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }
    
    if (activeTab === 'jobs') {
      return (
        <>
          <input placeholder="Job Title" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
          <select value={formData.department || ''} onChange={e => setFormData({...formData, department: e.target.value})} style={inputStyle}>
            <option value="">Select Department</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input placeholder="Location" value={formData.location || ''} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
          <select value={formData.employment_type || ''} onChange={e => setFormData({...formData, employment_type: e.target.value})} style={inputStyle}>
            <option value="">Employment Type</option>
            {employmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={formData.experience_level || ''} onChange={e => setFormData({...formData, experience_level: e.target.value})} style={inputStyle}>
            <option value="">Experience Level</option>
            {experienceLevels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <div style={{display: 'flex', gap: 10}}>
            <input placeholder="Min Salary" type="number" value={formData.salary_min || ''} onChange={e => setFormData({...formData, salary_min: parseFloat(e.target.value)})} style={{...inputStyle, flex: 1}} />
            <input placeholder="Max Salary" type="number" value={formData.salary_max || ''} onChange={e => setFormData({...formData, salary_max: parseFloat(e.target.value)})} style={{...inputStyle, flex: 1}} />
            <select value={formData.salary_currency || 'USD'} onChange={e => setFormData({...formData, salary_currency: e.target.value})} style={{...inputStyle, width: '80px'}}>
              <option value="USD">USD</option><option value="EUR">EUR</option><option value="NGN">NGN</option>
            </select>
          </div>
          <textarea placeholder="Job Description" rows="4" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} style={inputStyle} />
          <textarea placeholder="Requirements (one per line)" rows="3" value={formData.requirements?.join('\n') || ''} onChange={e => setFormData({...formData, requirements: e.target.value.split('\n').filter(l => l.trim())})} style={inputStyle} />
          <textarea placeholder="Benefits (one per line)" rows="3" value={formData.benefits?.join('\n') || ''} onChange={e => setFormData({...formData, benefits: e.target.value.split('\n').filter(l => l.trim())})} style={inputStyle} />
          <input type="date" placeholder="Closing Date" value={formData.closing_date || ''} onChange={e => setFormData({...formData, closing_date: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'open'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="open">Open</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="on-hold">On Hold</option>
          </select>
        </>
      )
    }
    
    if (activeTab === 'applicants') {
      return (
        <>
          <div style={{display: 'flex', gap: 10}}>
            <input placeholder="First Name" value={formData.first_name || ''} onChange={e => setFormData({...formData, first_name: e.target.value})} required style={{...inputStyle, flex: 1}} />
            <input placeholder="Last Name" value={formData.last_name || ''} onChange={e => setFormData({...formData, last_name: e.target.value})} required style={{...inputStyle, flex: 1}} />
          </div>
          <input placeholder="Email" type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} required style={inputStyle} />
          <input placeholder="Phone" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} style={inputStyle} />
          <select value={formData.job_id || ''} onChange={e => setFormData({...formData, job_id: e.target.value})} required style={inputStyle}>
            <option value="">Select Job ({jobs.filter(j => j.status === 'open' || j.status === 'active').length} open jobs)</option>
            {jobs.filter(j => j.status === 'open' || j.status === 'active').map(j => <option key={j.id} value={j.id}>{j.title} - {j.department}</option>)}
          </select>
          <input placeholder="Years of Experience" type="number" value={formData.experience_years || ''} onChange={e => setFormData({...formData, experience_years: parseInt(e.target.value)})} style={inputStyle} />
          <input placeholder="Skills (comma separated)" value={formData.skills?.join(', ') || ''} onChange={e => setFormData({...formData, skills: e.target.value.split(',').map(s => s.trim()).filter(s => s)})} style={inputStyle} />
          <input placeholder="Expected Salary" type="number" value={formData.expected_salary || ''} onChange={e => setFormData({...formData, expected_salary: parseFloat(e.target.value)})} style={inputStyle} />
          <select value={formData.salary_currency || 'USD'} onChange={e => setFormData({...formData, salary_currency: e.target.value})} style={inputStyle}>
            <option value="USD">USD</option><option value="EUR">EUR</option><option value="NGN">NGN</option>
          </select>
          <input placeholder="Portfolio URL" value={formData.portfolio_url || ''} onChange={e => setFormData({...formData, portfolio_url: e.target.value})} style={inputStyle} />
          <input placeholder="LinkedIn URL" value={formData.linkedin_url || ''} onChange={e => setFormData({...formData, linkedin_url: e.target.value})} style={inputStyle} />
          <textarea placeholder="Cover Letter" rows="3" value={formData.cover_letter || ''} onChange={e => setFormData({...formData, cover_letter: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'pending'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            {applicantStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{marginTop: 10}}>
            <label style={uploadLabel}>
              📄 Upload Resume (PDF)
              <input type="file" accept=".pdf" onChange={async (e) => {
                if (e.target.files[0]) {
                  const url = await handleResumeUpload(e.target.files[0])
                  if (url) setFormData({...formData, resume_url: url})
                }
              }} style={{display: 'none'}} />
            </label>
            {uploading && <p style={{fontSize: 12, color: '#64748b', marginTop: 5}}>Uploading...</p>}
            {formData.resume_url && <p style={{fontSize: 12, color: '#059669', marginTop: 5}}>✅ Resume uploaded</p>}
          </div>
        </>
      )
    }
    
    if (activeTab === 'interviews') {
      return (
        <>
          <select value={formData.applicant_id || ''} onChange={e => setFormData({...formData, applicant_id: e.target.value})} required style={inputStyle}>
            <option value="">Select Applicant</option>
            {applicants.filter(a => a.status !== 'rejected' && a.status !== 'hired').map(a => (
              <option key={a.id} value={a.id}>{a.first_name} {a.last_name} - {a.job_postings?.title}</option>
            ))}
          </select>
          <select value={formData.interview_type || ''} onChange={e => setFormData({...formData, interview_type: e.target.value})} required style={inputStyle}>
            <option value="">Interview Type</option>
            {interviewTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="datetime-local" value={formData.scheduled_date ? new Date(formData.scheduled_date).toISOString().slice(0, 16) : ''} onChange={e => setFormData({...formData, scheduled_date: e.target.value})} required style={inputStyle} />
          <input placeholder="Meeting Link (Zoom/Google Meet)" value={formData.meeting_link || ''} onChange={e => setFormData({...formData, meeting_link: e.target.value})} style={inputStyle} />
          <input placeholder="Location (if onsite)" value={formData.location || ''} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
          <input placeholder="Duration (minutes)" type="number" value={formData.duration_minutes || 60} onChange={e => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} style={inputStyle} />
          <select value={formData.status || 'scheduled'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </select>
          <textarea placeholder="Interview Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
          <textarea placeholder="Feedback / Evaluation" rows="3" value={formData.feedback || ''} onChange={e => setFormData({...formData, feedback: e.target.value})} style={inputStyle} />
        </>
      )
    }
    
    if (activeTab === 'offers') {
      return (
        <>
          <select value={formData.applicant_id || ''} onChange={e => setFormData({...formData, applicant_id: e.target.value})} required style={inputStyle}>
            <option value="">Select Applicant</option>
            {applicants.filter(a => a.status === 'interviewed' || a.status === 'offered' || a.status === 'shortlisted').map(a => (
              <option key={a.id} value={a.id}>{a.first_name} {a.last_name} - {a.job_postings?.title}</option>
            ))}
          </select>
          <input placeholder="Salary Offered" type="number" value={formData.salary_offered || ''} onChange={e => setFormData({...formData, salary_offered: parseFloat(e.target.value)})} required style={inputStyle} />
          <select value={formData.salary_currency || 'USD'} onChange={e => setFormData({...formData, salary_currency: e.target.value})} style={inputStyle}>
            <option value="USD">USD</option><option value="EUR">EUR</option><option value="NGN">NGN</option>
          </select>
          <input type="date" placeholder="Start Date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} required style={inputStyle} />
          <textarea placeholder="Benefits (one per line)" rows="3" value={formData.benefits?.join('\n') || ''} onChange={e => setFormData({...formData, benefits: e.target.value.split('\n').filter(l => l.trim())})} style={inputStyle} />
          <select value={formData.status || 'draft'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="draft">Draft</option>
            <option value="sent">Sent to Candidate</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
          </select>
          <textarea placeholder="Notes" rows="2" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    return null
  }

  const renderList = () => {
    if (loading) return <p>Loading...</p>
    
    const cardStyle = { background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }
    const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', marginRight: 8 }
    const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }
    
    // JOBS
    if (activeTab === 'jobs') {
      if (jobs.length === 0) return <p>No job postings yet. Click "+ Add New" to create one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {jobs.map(j => (
            <div key={j.id} style={cardStyle}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h3 style={{margin: 0}}>{j.title}</h3>
                <span style={{background: j.status === 'open' || j.status === 'active' ? '#d1fae5' : '#fee2e2', padding: '4px 12px', borderRadius: 20, fontSize: 12}}>
                  {j.status || 'draft'}
                </span>
              </div>
              <p style={{margin: '4px 0'}}>{j.department} • {j.location} • {j.employment_type}</p>
              <p style={{margin: '4px 0'}}>💰 {j.salary_currency} {j.salary_min?.toLocaleString()} - {j.salary_max?.toLocaleString()}</p>
              <p style={{margin: '4px 0', fontSize: 13, color: '#64748b'}}>📅 Closing: {j.closing_date ? new Date(j.closing_date).toLocaleDateString() : 'Not set'}</p>
              <div style={{marginTop: 10}}>
                <button onClick={() => { setEditingItem(j); setFormData(j); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(j.id)} style={deleteBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    // APPLICANTS
    if (activeTab === 'applicants') {
      if (applicants.length === 0) return <p>No applicants yet. Click "+ Add New" to add one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {applicants.map(a => (
            <div key={a.id} style={cardStyle}>
              <h3 style={{margin: '0 0 8px 0'}}>{a.first_name} {a.last_name}</h3>
              <p style={{margin: '2px 0'}}>📧 {a.email}</p>
              <p style={{margin: '2px 0'}}>📞 {a.phone || 'N/A'}</p>
              <p style={{margin: '2px 0'}}>🎯 Job: {a.job_postings?.title || 'Not assigned'}</p>
              <p style={{margin: '2px 0'}}>💼 Experience: {a.experience_years || 0} years</p>
              <p style={{margin: '2px 0'}}>Status: <span style={{background: a.status === 'hired' ? '#d1fae5' : a.status === 'rejected' ? '#fee2e2' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{a.status}</span></p>
              {a.resume_url && <p style={{margin: '2px 0'}}>📄 <a href={a.resume_url} target="_blank" rel="noopener noreferrer" style={{color: '#2c7da0'}}>View Resume</a></p>}
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10}}>
                <select onChange={(e) => updateApplicantStatus(a.id, e.target.value)} value={a.status || 'pending'} style={{padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12}}>
                  {applicantStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => { setSelectedApplicant(a); setEmailCompose({ to: a.email, subject: '', body: '', applicant_id: a.id }); setShowEmailModal(true) }} style={{...editBtn, background: '#059669'}}>📧 Email</button>
                <WhatsAppButton type="applicant" data={a} phoneNumber={a.phone} session={session} companyName={companyName} buttonText="📱 WhatsApp" />
                <button onClick={() => { setEditingItem(a); setFormData(a); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(a.id)} style={deleteBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    // INTERVIEWS
    if (activeTab === 'interviews') {
      if (interviews.length === 0) return <p>No interviews scheduled yet.</p>
      return (
        <div style={responsiveStyles.grid}>
          {interviews.map(i => {
            const applicant = applicants.find(a => a.id === i.applicant_id)
            return (
              <div key={i.id} style={cardStyle}>
                <h3 style={{margin: '0 0 8px 0'}}>📅 {i.scheduled_date ? new Date(i.scheduled_date).toLocaleString() : 'Date not set'}</h3>
                <p style={{margin: '2px 0'}}>👤 {i.applicants?.first_name} {i.applicants?.last_name}</p>
                <p style={{margin: '2px 0'}}>🎯 {i.job_postings?.title}</p>
                <p style={{margin: '2px 0'}}>📞 {i.interview_type} • {i.status}</p>
                {i.meeting_link && <p style={{margin: '2px 0'}}>🔗 <a href={i.meeting_link} target="_blank" rel="noopener noreferrer" style={{color: '#2c7da0'}}>Join Meeting</a></p>}
                <div style={{display: 'flex', gap: 8, marginTop: 10}}>
                  <WhatsAppButton type="interview" data={{ ...i, applicant_name: `${i.applicants?.first_name} ${i.applicants?.last_name}` }} phoneNumber={applicant?.phone} session={session} companyName={companyName} buttonText="📱 Remind" />
                  <button onClick={() => { setEditingItem(i); setFormData(i); setShowModal(true) }} style={editBtn}>Edit</button>
                  <button onClick={() => handleDelete(i.id)} style={deleteBtn}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    
    // OFFERS
    if (activeTab === 'offers') {
      if (offers.length === 0) return <p>No offers yet.</p>
      return (
        <div style={responsiveStyles.grid}>
          {offers.map(o => {
            const applicant = applicants.find(a => a.id === o.applicant_id)
            return (
              <div key={o.id} style={cardStyle}>
                <h3 style={{margin: '0 0 8px 0'}}>📄 Offer for {o.applicants?.first_name} {o.applicants?.last_name}</h3>
                <p style={{margin: '2px 0'}}>🎯 Position: {o.job_postings?.title}</p>
                <p style={{margin: '2px 0'}}>💰 Salary: {o.salary_currency} {o.salary_offered?.toLocaleString()}</p>
                <p style={{margin: '2px 0'}}>📅 Start Date: {o.start_date ? new Date(o.start_date).toLocaleDateString() : 'Not set'}</p>
                <p style={{margin: '2px 0'}}>Status: <span style={{background: o.status === 'accepted' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{o.status}</span></p>
                <div style={{display: 'flex', gap: 8, marginTop: 10}}>
                  <WhatsAppButton type="offer" data={{ ...o, applicant_name: `${o.applicants?.first_name} ${o.applicants?.last_name}` }} phoneNumber={applicant?.phone} session={session} companyName={companyName} buttonText="📱 Share Offer" />
                  <button onClick={() => { setEditingItem(o); setFormData(o); setShowModal(true) }} style={editBtn}>Edit</button>
                  <button onClick={() => handleDelete(o.id)} style={deleteBtn}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    return null
  }

  // Styles
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 25, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  const statCard = { background: '#fff', padding: 20, borderRadius: 16, textAlign: 'center', border: '1px solid #e2e8f0' }
  const statNumber = { fontSize: 36, fontWeight: 'bold', color: '#1e3a5f', marginTop: 10 }
  const recentCard = { background: '#fff', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0', marginBottom: 30 }
  const recentItem = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e2e8f0', gap: 10, flexWrap: 'wrap' }
  const inputStyle = { width: '100%', padding: 10, margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: 25, borderRadius: 20, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer' }
  const uploadLabel = { display: 'inline-block', background: '#2c7da0', color: '#fff', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }

  if (loading && !companyId) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading company data...</div>
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Recruitment Portal"
      portalIcon="🎯"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlayStyle} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div style={modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2>📧 {selectedApplicant ? `Send Email to ${selectedApplicant.first_name} ${selectedApplicant.last_name}` : 'Compose Email'}</h2>
            <input 
              placeholder="To" 
              value={emailCompose.to} 
              onChange={e => setEmailCompose({...emailCompose, to: e.target.value})} 
              style={inputStyle} 
            />
            <input 
              placeholder="Subject" 
              value={emailCompose.subject} 
              onChange={e => setEmailCompose({...emailCompose, subject: e.target.value})} 
              style={inputStyle} 
            />
            <textarea 
              placeholder="Message" 
              rows="8" 
              value={emailCompose.body} 
              onChange={e => setEmailCompose({...emailCompose, body: e.target.value})} 
              style={inputStyle} 
            />
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={sendEmail} style={saveBtn}>Send Email</button>
              <button onClick={() => setShowEmailModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1>{activeTab === 'dashboard' ? 'Recruitment Dashboard' : activeTab === 'whatsapp' ? 'WhatsApp Communication' : activeTab === 'emails' ? 'Email Communication' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          <p style={{color: '#64748b'}}>Welcome, {session.user.email} - {companyName} Recruitment</p>
        </div>
        {activeTab !== 'dashboard' && activeTab !== 'whatsapp' && activeTab !== 'emails' && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>
            + Add New
          </button>
        )}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          <div style={responsiveStyles.statsGrid}>
            <div style={statCard}><h3>📋 Total Jobs</h3><p style={statNumber}>{stats.totalJobs}</p><small>{stats.openJobs} open</small></div>
            <div style={statCard}><h3>👥 Applicants</h3><p style={statNumber}>{stats.totalApplicants}</p><small>{stats.pendingApplicants} pending</small></div>
            <div style={statCard}><h3>📅 Interviews</h3><p style={statNumber}>{stats.scheduledInterviews}</p><small>Scheduled</small></div>
            <div style={statCard}><h3>📄 Offers</h3><p style={statNumber}>{stats.offersSent}</p><small>Sent</small></div>
            <div style={statCard}><h3>🎉 Hired</h3><p style={{...statNumber, color: '#059669'}}>{stats.hiredCount}</p><small>Total hires</small></div>
          </div>
          
          <div style={responsiveStyles.twoColumnGrid}>
            <div style={recentCard}>
              <h3>Recent Applicants</h3>
              {applicants.slice(0, 5).map(a => (
                <div key={a.id} style={recentItem}>
                  <span><strong>{a.first_name} {a.last_name}</strong> - {a.job_postings?.title}</span>
                  <span style={{fontSize: 12, color: '#64748b'}}>{new Date(a.applied_date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
            <div style={recentCard}>
              <h3>Upcoming Interviews</h3>
              {interviews.filter(i => i.status === 'scheduled').slice(0, 5).map(i => (
                <div key={i.id} style={recentItem}>
                  <span>{i.applicants?.first_name} {i.applicants?.last_name}</span>
                  <span style={{fontSize: 12}}>{i.scheduled_date ? new Date(i.scheduled_date).toLocaleDateString() : 'Date TBD'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ marginTop: 20 }}>
        {['jobs', 'applicants', 'interviews', 'offers'].includes(activeTab) && renderList()}
      </div>

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div>
          <h2 style={{fontSize: '24px', color: '#1e3a5f'}}>📱 WhatsApp Communication - {companyName}</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📋 Share Job Opening</h3>
              <p>Send job openings via WhatsApp</p>
              {jobs.filter(j => j.status === 'open' || j.status === 'active').slice(0, 5).map(job => (
                <div key={job.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{job.title}</strong> - {job.department}</p>
                  <WhatsAppButton type="job" data={job} phoneNumber="" session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>👥 Contact Applicants</h3>
              <p>Send messages to applicants</p>
              {applicants.slice(0, 5).map(applicant => (
                <div key={applicant.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{applicant.first_name} {applicant.last_name}</strong> - {applicant.job_postings?.title}</p>
                  <WhatsAppButton type="applicant" data={applicant} phoneNumber={applicant.phone} session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📅 Interview Reminders</h3>
              <p>Send interview reminders to candidates</p>
              {interviews.filter(i => i.status === 'scheduled').slice(0, 5).map(interview => {
                const applicant = applicants.find(a => a.id === interview.applicant_id)
                return (
                  <div key={interview.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{applicant?.first_name} {applicant?.last_name}</strong> - {new Date(interview.scheduled_date).toLocaleString()}</p>
                    <WhatsAppButton type="interview" data={interview} phoneNumber={applicant?.phone} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📄 Share Offer</h3>
              <p>Send job offers via WhatsApp</p>
              {offers.filter(o => o.status === 'sent' || o.status === 'accepted').slice(0, 5).map(offer => {
                const applicant = applicants.find(a => a.id === offer.applicant_id)
                return (
                  <div key={offer.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{applicant?.first_name} {applicant?.last_name}</strong> - {offer.salary_currency} {offer.salary_offered?.toLocaleString()}</p>
                    <WhatsAppButton type="offer" data={offer} phoneNumber={applicant?.phone} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Emails Tab */}
      {activeTab === 'emails' && (
        <div>
          <h2 style={{fontSize: '24px', color: '#1e3a5f'}}>📧 Email Communication - {companyName}</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>✏️ Compose New Email</h3>
              <p>Send emails to applicants or contacts</p>
              <button 
                onClick={() => {
                  setSelectedApplicant(null)
                  setEmailCompose({ to: '', subject: '', body: '', applicant_id: null })
                  setShowEmailModal(true)
                }} 
                style={addBtn}
              >
                📧 Compose Email
              </button>
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>👥 Quick Email to Applicants</h3>
              <p>Send emails to your applicants</p>
              {applicants.slice(0, 5).map(applicant => (
                <div key={applicant.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{applicant.first_name} {applicant.last_name}</strong> - {applicant.job_postings?.title}</p>
                  <button 
                    onClick={() => {
                      setSelectedApplicant(applicant)
                      setEmailCompose({ 
                        to: applicant.email, 
                        subject: '', 
                        body: '', 
                        applicant_id: applicant.id 
                      })
                      setShowEmailModal(true)
                    }} 
                    style={{...editBtn, background: '#059669'}}
                  >
                    📧 Send Email
                  </button>
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📝 Email Templates</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Interview Invitation - ${companyName}`,
                      body: `Dear Candidate,\n\nWe were impressed with your application and would like to invite you for an interview at ${companyName}.\n\n📅 Date: [Date]\n🕐 Time: [Time]\n📍 Location: [Location/Meeting Link]\n\nPlease confirm your availability.\n\nBest regards,\n${companyName} Recruitment Team`,
                      applicant_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{...editBtn, background: '#1e3a5f', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Interview Invitation Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Job Offer from ${companyName}`,
                      body: `Dear Candidate,\n\nWe are pleased to offer you the position of [Job Title] at ${companyName}.\n\n💰 Salary: [Amount]\n📅 Start Date: [Date]\n\nPlease review the attached offer letter.\n\nWe look forward to having you on our team!\n\nBest regards,\n${companyName} Recruitment Team`,
                      applicant_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{...editBtn, background: '#1e3a5f', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Job Offer Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Application Update - ${companyName}`,
                      body: `Dear Candidate,\n\nThank you for your application for the position of [Job Title] at ${companyName}.\n\nWe have reviewed your application and would like to move forward with the next steps.\n\nWe will contact you shortly with more information.\n\nBest regards,\n${companyName} Recruitment Team`,
                      applicant_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{...editBtn, background: '#f59e0b', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Application Update Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Application Status - ${companyName}`,
                      body: `Dear Candidate,\n\nThank you for your interest in the position of [Job Title] at ${companyName}.\n\nAfter careful consideration, we have decided to move forward with other candidates whose qualifications more closely match our current needs.\n\nWe wish you the best in your job search.\n\nBest regards,\n${companyName} Recruitment Team`,
                      applicant_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{...editBtn, background: '#dc2626', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Rejection Notice Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? '✏️ Edit' : '➕ Add New'} {activeTab.slice(0, -1)}</h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{display: 'flex', gap: 10, marginTop: 20}}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default RecruitmentPortal